/**
 * Descobre quem é o representante de cada venda (pelo código do vendedor na
 * fonte e pelo mês da venda) e qual é a marca (pelo nome da marca no arquivo).
 *
 * A ligação código → representante fica no cadastro, não dentro da venda. Se a
 * gerente corrigir um código depois, as vendas antigas passam a contar certo
 * sem importar de novo.
 *
 * Representantes mudam; os clientes (e o código) ficam com quem assume. Por
 * isso cada ligação tem um "desde" (mês). Vale a ligação mais recente que já
 * começou no mês da venda. Ex.: código 42 → Fabricia (desde sempre) e
 * → Tatielly (desde 2026-09): venda de agosto = Fabricia; de setembro = Tatielly.
 *
 * Lê: os cadastros que recebe.
 * Usado por: dominio/painel.ts, importacao/resumo.ts (prévia) e telas/cadastros/ (códigos e "saiu da equipe").
 */
import { mesesDepois } from './datas.ts';
import type { CodigoRepresentante, Fonte, Marca, Mes } from './tipos.ts';

/** Tira acentos, espaços repetidos e diferença de maiúsculas: "  Alysson  " → "ALYSSON". */
export function normalizarNome(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
}

/** Códigos numéricos perdem zeros à esquerda ("003" → "3"); os demais viram normalizarNome. */
export function normalizarCodigo(codigo: string): string {
  const limpo = codigo.trim();
  if (/^\d+$/.test(limpo)) return limpo.replace(/^0+(?=\d)/, '');
  return normalizarNome(limpo);
}

/** As ligações de cada código (chave "fonte|código"), da mais antiga para a mais nova. */
function agruparPorCodigo(codigos: CodigoRepresentante[]): Map<string, CodigoRepresentante[]> {
  const grupos = new Map<string, CodigoRepresentante[]>();
  for (const item of codigos) {
    const chave = `${item.fonte}|${normalizarCodigo(item.codigo)}`;
    grupos.set(chave, [...(grupos.get(chave) ?? []), item]);
  }
  // "desde sempre" (null) vem primeiro: '' é menor que qualquer mês.
  for (const lista of grupos.values()) lista.sort((a, b) => (a.desde ?? '').localeCompare(b.desde ?? ''));
  return grupos;
}

/** Cria uma função que responde: este código, desta fonte, era de qual representante neste mês? */
export function criarIdentificador(codigos: CodigoRepresentante[]): (fonte: Fonte, codigo: string, mes: Mes) => number | null {
  const grupos = agruparPorCodigo(codigos);
  return (fonte, codigo, mes) => {
    let dono: number | null = null;
    for (const ligacao of grupos.get(`${fonte}|${normalizarCodigo(codigo)}`) ?? []) {
      if ((ligacao.desde ?? '') <= mes) dono = ligacao.representanteId;
    }
    return dono;
  };
}

const porFonteECodigo = (a: { fonte: Fonte; codigo: string }, b: { fonte: Fonte; codigo: string }) =>
  a.fonte.localeCompare(b.fonte) || a.codigo.localeCompare(b.codigo, 'pt-BR', { numeric: true });

/** A ligação que vale no mês (a mais recente que já começou), de uma lista em ordem. */
function ligacaoNoMes(lista: CodigoRepresentante[], mes: Mes): CodigoRepresentante | undefined {
  return lista.filter((ligacao) => (ligacao.desde ?? '') <= mes).at(-1);
}

/**
 * Os códigos (de todas as fontes) que são deste representante no mês. Volta o
 * texto como está na ligação que vale no mês (é ele que o servidor reconhece).
 * Usado na tela do representante ("Código no Winthor: 56").
 */
export function codigosDoRepresentanteNoMes(codigos: CodigoRepresentante[], representanteId: number, mes: Mes): Array<{ fonte: Fonte; codigo: string }> {
  return [...agruparPorCodigo(codigos).values()]
    .map((lista) => ligacaoNoMes(lista, mes))
    .filter((ligacao): ligacao is CodigoRepresentante => ligacao !== undefined && ligacao.representanteId === representanteId)
    .map((ligacao) => ({ fonte: ligacao.fonte, codigo: ligacao.codigo }))
    .sort(porFonteECodigo);
}

/**
 * "Saiu da equipe": os códigos que passam para quem entra a partir do mês. Os que
 * são da pessoa no mês e também os que ela só recebe depois (de uma troca já marcada):
 * os códigos passam junto com as metas daquele mês em diante.
 */
export function codigosParaPassar(codigos: CodigoRepresentante[], representanteId: number, mes: Mes): Array<{ fonte: Fonte; codigo: string; recebeEm?: Mes }> {
  return [...agruparPorCodigo(codigos).values()]
    .map((lista) => {
      const noMes = ligacaoNoMes(lista, mes);
      if (noMes?.representanteId === representanteId) return { fonte: noMes.fonte, codigo: noMes.codigo };
      const depois = lista.find((ligacao) => ligacao.desde !== null && ligacao.desde > mes && ligacao.representanteId === representanteId);
      // recebeEm: a pessoa só ganharia esse código depois; ele passa a partir desse mês.
      return depois ? { fonte: depois.fonte, codigo: depois.codigo, recebeEm: depois.desde ?? undefined } : undefined;
    })
    .filter((item): item is { fonte: Fonte; codigo: string; recebeEm?: Mes } => item !== undefined)
    .sort(porFonteECodigo);
}

/** Uma ligação com o período em que vale (para mostrar na tela). */
export interface PeriodoDaLigacao extends CodigoRepresentante {
  /** Último mês em que vale (o mês antes da próxima ligação do mesmo código). null = até hoje. */
  ate: Mes | null;
}

/** Cada código de uma fonte com as suas ligações em ordem: "42: Fabricia até 2026-08, Tatielly desde 2026-09". */
export function linhaDoTempoDosCodigos(codigos: CodigoRepresentante[], fonte: Fonte): Array<{ codigo: string; ligacoes: PeriodoDaLigacao[] }> {
  return [...agruparPorCodigo(codigos.filter((item) => item.fonte === fonte)).values()]
    .map((lista) => ({
      // Para mostrar, o texto como foi cadastrado ("GO - MEIRELES - Alyson Moraes Vieira");
      // a comparação (agruparPorCodigo) é que ignora acentos e maiúsculas.
      codigo: lista[0]?.codigo ?? '',
      ligacoes: lista.map((ligacao, i) => {
        const proxima = lista[i + 1];
        return { ...ligacao, ate: proxima?.desde ? mesesDepois(proxima.desde, -1) : null };
      }),
    }))
    .sort((a, b) => a.codigo.localeCompare(b.codigo, 'pt-BR', { numeric: true }));
}

/** Cria uma função que responde: esta marca, como veio no arquivo, é qual marca do cadastro? */
export function criarIdentificadorDeMarca(marcas: Marca[]): (marcaOrigem: string | null) => number | null {
  const mapa = new Map<string, number>();
  for (const marca of marcas) mapa.set(normalizarNome(marca.nome), marca.id);
  return (marcaOrigem) => (marcaOrigem === null ? null : (mapa.get(normalizarNome(marcaOrigem)) ?? null));
}
